import React from "react";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import { Box, Typography } from "@mui/material";
import NumericInputField from "../common/NumericInputField";
import CustomSelect from "../common/CustomSelect";
import IOSSwitch from "../common/IOSSwitch";
import ScrollableWrapper from "../common/ScrollableWrapper";
import { ServiceDetails, Location } from "./types";
import { enSvcUBillingType } from "./helper";
import PinIconSvg from "../../../../common/svgs/PinIconSvg";

type LocationCardProps = {
  location: Location;
  isSelected: boolean;
  onToggle: (locationId: string) => void;
};

const LocationCard = React.memo<LocationCardProps>(({
  location,
  isSelected,
  onToggle,
}) => {
  const theme: Theme = useTheme();
  return (
    <Box
      sx={styles.locationCard(theme, isSelected)}
      onClick={() => onToggle(location.id)}
    >
      <Box sx={styles.locationIcon}>
        <PinIconSvg />
      </Box>
      <Box sx={styles.locationInfo}>
        <Typography sx={styles.locationTitle(theme)}>
          {location.title}
        </Typography>
        <Typography sx={styles.locationAddress(theme)}>
          {location.address}
        </Typography>
        <Typography sx={styles.locationAddress(theme)}>
          {location.city}
        </Typography>
      </Box>
    </Box>
  );
});

type Props = {
  serviceDetails: ServiceDetails;
  setServiceDetails: (
    value: ServiceDetails | ((prev: ServiceDetails) => ServiceDetails)
  ) => void;
  availableLocations: Location[];
};

const ServiceBuilderStep4 = ({
  serviceDetails,
  setServiceDetails,
  availableLocations,
}: Props) => {
  const theme: Theme = useTheme();

  // Helper function to parse selected locations
  const parseSelectedLocations = (selectedLocations: string): string[] => {
    return selectedLocations ? selectedLocations.split(",").filter(id => id.trim()) : [];
  };

  const handleLocationToggle = (locationId: string) => {
    setServiceDetails((prev) => {
      const currentLocations = parseSelectedLocations(prev.selectedLocations);
      const isSelected = currentLocations.includes(locationId);

      const newLocations = isSelected
        ? currentLocations.filter((id) => id !== locationId)
        : [...currentLocations, locationId];

      return {
        ...prev,
        selectedLocations: newLocations.join(","),
      };
    });
  };

  const handleVisibilityToggle = (checked: boolean) => {
    setServiceDetails((prev) => ({
      ...prev,
      locatorVisible: checked ? 1 : 0,
      ...(!checked && {
        basePrice: 0,
        billingType: 0,
      }),
    }));
  };

  const handlePriceChange = (value: string) => {
    setServiceDetails((prev) => ({
      ...prev,
      basePrice: parseFloat(value) || 0,
    }));
  };

  const handleBillingChange = (value: string | null) => {
    const billingKey = Object.entries(enSvcUBillingType)
      .find(([_, label]) => label === value)?.[0];
    
    if (billingKey) {
      setServiceDetails((prev) => ({
        ...prev,
        billingType: Number(billingKey),
      }));
    }
  };


  return (
    <ScrollableWrapper>
      <Box sx={styles.container}>
        {/* Location Section */}
        <Box sx={styles.section}>
          <Box>
            <Typography sx={styles.title(theme)}>Location(s)</Typography>
            <Typography sx={styles.subtitle(theme)}>
              Select where this service will be available
            </Typography>
          </Box>
          <Box sx={styles.locationsContainer}>
            {availableLocations.map((location) => (
              <LocationCard
                key={location.id}
                location={location}
                isSelected={
                  parseSelectedLocations(serviceDetails.selectedLocations).includes(location.id)
                }
                onToggle={handleLocationToggle}
              />
            ))}
          </Box>
        </Box>

        {/* PNOE locator price Section */}
        <Box sx={styles.priceSection(theme)}>
          <Box>
            <Box sx={styles.sectionHeader}>
              <Typography sx={styles.title(theme)}>
                PNOĒ locator price
              </Typography>
              <IOSSwitch
                checked={serviceDetails.locatorVisible === 1}
                onChange={(e) => handleVisibilityToggle(e.target.checked)}
                leftLabel="Invisible"
                rightLabel="Visible"
                showLabels={true}
              />
            </Box>
            <Typography sx={styles.subtitle(theme)}>
              Publish this service on the Locator. The price you set will appear
              as the reference price.
            </Typography>
          </Box>

          {serviceDetails.locatorVisible === 1 && (
            <>
              <Box sx={styles.inputsContainer}>
                <Box sx={styles.inputWrapper}>
                  <NumericInputField
                    label="Price"
                    value={serviceDetails.basePrice?.toString() || ""}
                    handleChange={(e) => handlePriceChange(e.target.value)}
                    allowNegative={false}
                  />
                </Box>
                <Box sx={styles.inputWrapper}>
                  <Box sx={styles.fixedCurrencyContainer(theme)}>
                    <Typography sx={styles.fixedCurrencyLabel(theme)}>
                      Currency
                    </Typography>
                    <Typography sx={styles.fixedCurrencyValue(theme)}>
                      $ USD
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={styles.inputWrapper}>
                <CustomSelect
                  label="Billing"
                  value={
                    enSvcUBillingType[
                      serviceDetails.billingType as keyof typeof enSvcUBillingType
                    ] || null
                  }
                  onChange={handleBillingChange}
                  options={Object.values(enSvcUBillingType)}
                />
              </Box>
            </>
          )}
        </Box>
        <Box sx={{ height: "20px" }} />
      </Box>
    </ScrollableWrapper>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "32px",
  },
  section: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    lineHeight: "20px",
  }),
  subtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    lineHeight: "17px",
    marginTop: "4px",
  }),
  locationsContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  locationCard: (theme: Theme, isSelected: boolean) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    padding: "12px",
    border: `1px solid ${
      isSelected
        ? theme.colors_ds.PRIMARY_ICON_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    borderRadius: "8px",
    backgroundColor: isSelected
      ? theme.colorTokens.P[10]
      : theme.colors_ds.NEUTRAL_BG_MAIN,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: isSelected
        ? theme.colorTokens.P[10]
        : theme.colorTokens.N[30],
    },
  }),
  locationIcon: {
    marginTop: "4px",
  },
  locationInfo: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "2px",
  },
  locationTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    lineHeight: "24px",
    marginBottom: "8px",
  }),
  locationAddress: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    lineHeight: "20px",
  }),
  priceSection: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    padding: "12px",
    borderRadius: "8px",
  }),
  inputsContainer: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap" as "wrap",
  },
  inputWrapper: {
    flex: 1,
    minWidth: "200px",
  },
  fixedCurrencyContainer: (theme: Theme) => ({
    position: "relative",
    height: "56px",
    minHeight: "56px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    padding: "16px 12px 10px 12px",
    display: "flex",
    flexDirection: "column" as "column",
  }),
  fixedCurrencyLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    position: "absolute",
    top: "6px",
    left: "12px",
  }),
  fixedCurrencyValue: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    marginTop: "8px",
  }),
};

// Helper function for parsing selected locations (shared with component)
const parseSelectedLocationsHelper = (selectedLocations: string): string[] => {
  return selectedLocations ? selectedLocations.split(",").filter(id => id.trim()) : [];
};

// Standalone validation function for Step 4
export const validateStep4 = (serviceDetails: ServiceDetails): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Check if at least one location is selected
  const selectedLocationIds = parseSelectedLocationsHelper(serviceDetails.selectedLocations);
  const hasSelectedLocations = selectedLocationIds.length > 0;

  if (!hasSelectedLocations) {
    errors.push("Please select at least one location");
  }

  // If locator is visible, validate price and billing
  if (serviceDetails.locatorVisible === 1) {
    if (!serviceDetails.basePrice || serviceDetails.basePrice <= 0) {
      errors.push("Please enter a valid price");
    }

    if (!serviceDetails.billingType) {
      errors.push("Please select a billing type");
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export default ServiceBuilderStep4;
